import { GetAddressResponse } from '@payer-admin-ui/api/src/customers/responses/GetAddressResponse';
import { CountryCode } from '@payer-admin-ui/api/src/invoices/models/CountryCode';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { CustomerType } from '@payer-admin-ui/api/src/invoices/models/CustomerType';
import { DeliveryType } from '@payer-admin-ui/api/src/invoices/models/DeliveryType';
import { InvoiceType } from '@payer-admin-ui/api/src/invoices/models/InvoiceType';
import { LanguageCode } from '@payer-admin-ui/api/src/invoices/models/LanguageCode';
import { PaymentMethod } from '@payer-admin-ui/api/src/invoices/models/PaymentMethod';
import {
  CreateInvoiceRequestV3,
  LineItemWithDecimalAmountExcludingVat,
} from '@payer-admin-ui/api/src/invoices/requests/CreateInvoiceRequestV3';
import { ResponseInvoiceV3 } from '@payer-admin-ui/api/src/invoices/responses/InvoiceResponseV3';
import { PayerError } from '@payer-admin-ui/components';
import { deepClone } from '@payer-admin-ui/components-v2/src/utils/values';
import { FetchingState } from '@payer-admin-ui/components-v2/src/types/state';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { invoiceAPI } from 'redux/invoiceAPI';
import { ItemType } from '@payer-admin-ui/api/src/invoices/models/ItemType';
import { defaultSerializeDate, getTodayCET } from '@payer-admin-ui/components-v2/src/utils/dates';
import { MerchantAdminState } from '../../../redux/store';
import { fetchInvoice } from './invoicesGlobalState';

// RHF will dispatch its values by reference (for nested objects), and if we simply
// put that into the slice state, those objects will become frozen so RHF will no longer be
// able to mutate them internally.
export const cloneInvoiceRequest = (invoice: CreateInvoiceRequestV3) => {
  return {
    ...invoice,
    invoiceCustomer: { ...invoice.invoiceCustomer },
  };
};

export const createInvoice = createAsyncThunk<
  ResponseInvoiceV3,
  { invoice: CreateInvoiceRequestV3; sendInvoice: boolean; draft: boolean },
  { state: MerchantAdminState }
>('invoices/createInvoice', async ({ invoice, sendInvoice, draft }, { dispatch }) => {
  if (invoice.partOfInvoiceStatement) {
    invoice.invoiceDate = undefined;
  }
  const response = await invoiceAPI.createInvoiceV3({ ...invoice, sendInvoice, draft });
  void dispatch(fetchInvoice({ invoiceNumber: response.invoiceNumber }));
  return response;
});

type GetAddressArgs = {
  regNumber: string | undefined;
  countryCode: CountryCode;
};

export const getAddress = createAsyncThunk<GetAddressResponse, GetAddressArgs, { state: MerchantAdminState }>(
  'invoices/getAddress',
  async ({ regNumber, countryCode }, _) => {
    if (!regNumber || !countryCode) {
      throw new PayerError({ message: 'Missing fields: registration number and country must be present' });
    }
    return invoiceAPI.getAddress({
      countryCode,
      regNumber,
    });
  },
);

export type CreateInvoicePageState = {
  invoice: CreateInvoiceRequestV3;
  createdInvoiceNumber?: string;
  addressFetchState: Omit<FetchingState, 'error'>;
  submit: Omit<FetchingState, 'error'>;
  error: any;
};

export const defaultItem: LineItemWithDecimalAmountExcludingVat = {
  itemType: ItemType.FREEFORM,
  position: 0,
  articleNumber: '',
  description: '',
  quantity: 1,
  unit: undefined,
  unitPriceExcludingVat: 0,
  vatPercentage: 0,
  salesAccountCode: undefined,
  vatAccountCode: undefined,
  additionalDetail: '',
  note: '',
  costCenter: '',
};

const initialInvoice = {
  type: InvoiceType.NORMAL,
  deliveryType: DeliveryType.EMAIL,
  draft: false,
  sendInvoice: false,
  partOfInvoiceStatement: false,
  invoiceDate: defaultSerializeDate(getTodayCET()),
  dueDays: 30,
  orderReference: '',
  externalInvoiceNumber: undefined,
  additionalReferenceId: undefined,
  comment: undefined,
  currencyCode: CurrencyCode.SEK,
  ourReference: undefined,
  yourReference: undefined,
  contractReference: undefined,
  yourOrderNumber: undefined,
  site: undefined,
  languageCode: LanguageCode.sv,
  paymentMethods: [PaymentMethod.BANK],
  invoiceCustomer: {
    customerType: CustomerType.ORGANISATION,
    regNumber: undefined,
    customerVatId: undefined,
    externalCustomerNumber: undefined,
    address: {
      streetAddress1: '',
      zipCode: '',
      city: '',
      countryCode: CountryCode.SE,
      companyName: undefined,
      yourReference: undefined,
      title: undefined,
      firstName: undefined,
      lastName: undefined,
      streetAddress2: undefined,
      coAddress: undefined,
      houseAddress: undefined,
      state: undefined,
      emailAddress: undefined,
      phoneNumber: undefined,
      emailAddressCc: undefined,
      emailAddressBcc: undefined,
      gln: undefined,
    },
  },
  items: [{ ...defaultItem }],
  roundOff: undefined,
  totalAmountExcludingVat: undefined,
  totalAmountIncludingVat: undefined,
} as CreateInvoiceRequestV3;

const initialState: CreateInvoicePageState = {
  invoice: initialInvoice,
  addressFetchState: { isFetching: false, success: false },
  submit: { isFetching: false, success: false },
  error: null,
};

const createInvoiceSlice = createSlice({
  name: 'invoices/create',
  initialState,
  reducers: {
    resetError: state => ({ ...state, error: null }),
    setError: (state, { payload: message }: PayloadAction<string>) => ({
      ...state,
      error: { message },
    }),
    resetInvoice: _ => {
      return {
        ...initialState,
        invoice: {
          ...initialInvoice,
          invoiceCustomer: {
            ...initialInvoice.invoiceCustomer,
            address: { ...initialInvoice.invoiceCustomer.address },
          },
          items: [{ ...defaultItem }],
        },
      };
    },
    patchInvoice: (state, { payload: invoicePatch }: PayloadAction<Partial<CreateInvoiceRequestV3>>) => {
      // Redux will freeze the values (at least in dev), and if they are coming from react-hook-form, that will break the form.
      const patch = deepClone(invoicePatch);

      state.invoice = {
        ...state.invoice,
        ...patch,
        invoiceCustomer: patch.invoiceCustomer
          ? {
              ...state.invoice.invoiceCustomer,
              ...patch.invoiceCustomer,
              address: patch.invoiceCustomer.address
                ? {
                    ...state.invoice.invoiceCustomer.address,
                    ...patch.invoiceCustomer.address,
                  }
                : state.invoice.invoiceCustomer.address,
            }
          : state.invoice.invoiceCustomer,
      };
    },
    resetGetAddressSuccess: state => {
      state.addressFetchState.success = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(createInvoice.pending, (state, _) => ({
      ...state,
      submit: { isFetching: true, success: false },
      error: null,
    }));
    builder.addCase(
      createInvoice.fulfilled,
      (state, { payload: { invoiceNumber } }: PayloadAction<ResponseInvoiceV3>) => ({
        ...state,
        submit: {
          isFetching: false,
          success: true,
        },
        createdInvoiceNumber: invoiceNumber.toString(),
      }),
    );
    builder.addCase(createInvoice.rejected, (state, { error }) => ({
      ...state,
      submit: { isFetching: false, success: false },
      error,
    }));

    builder.addCase(getAddress.pending, (state, _) => ({
      ...state,
      addressFetchState: { isFetching: true, success: false },
      error: null,
    }));
    builder.addCase(getAddress.fulfilled, (state, _) => {
      return {
        ...state,
        addressFetchState: { isFetching: false, success: true },
      };
    });
    builder.addCase(getAddress.rejected, (state, { error }) => ({
      ...state,
      addressFetchState: { success: false, isFetching: false },
      error,
    }));
  },
});

export const {
  actions: { setError, resetError, resetInvoice, resetGetAddressSuccess, patchInvoice },
  reducer: createInvoiceReducer,
} = createInvoiceSlice;
